// A store's shopping assistant, version by version, the way an App's Versions tab shows an App
// (apps/versions/versions-tab.tsx): newest first and grouped by day, the live one marked, Publish on
// the newest draft and Restore… on an older one. Any version is compared with the live one by a
// slider over the two real previews, with what changed between their designs listed beside it.
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { StatusPill } from '@/components/ui/kit';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy, { type StorefrontCopyKey } from '@/i18n/ns/storefront';
import { ErrorState, LoadingState } from '../primitives';
import '../apps/versions/versions-tab.css';
import {
  useStorefrontApi,
  type StorefrontApi,
  type StoreDesign,
  type StoreDetail,
  type StoreVersion,
} from './api';
import { designChanges, isColour, type DesignChange, type DesignValue } from './design-diff';
import { useRemote } from './use-storefront';

type Copy = Readonly<Record<StorefrontCopyKey, string>>;

const FRAME_SANDBOX = 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox';

export function VersionsView({
  store,
  publishHref,
  onRestored,
}: {
  store: StoreDetail;
  /** Where publishing happens (the Install tab, where publishing takes a tap). */
  publishHref: string;
  onRestored?: (version: number) => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const api = useStorefrontApi();
  const locale = useAppCatalogLocale();
  const [restoredTick, setRestoredTick] = useState(0);
  const pagesKey = `${store.id}:${store.draft?.version ?? 0}:${store.live?.version ?? 0}:${restoredTick}`;
  const { list, versions, live, more } = useVersionPages(api, store.id, pagesKey);
  const [picked, setPicked] = useState<number | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);
  const [restoring, setRestoring] = useState<'idle' | 'pending' | 'failed'>('idle');
  const newest = versions[0]?.version ?? null;
  const to =
    picked !== null && versions.some((entry) => entry.version === picked) ? picked : newest;
  const older = (version: number) =>
    versions.find((entry) => entry.version < version)?.version ?? null;
  // Compared with the live version; the live one itself, and a store with nothing live, with the one before.
  const from = to === null ? null : to === live || live === null ? older(to) : live;
  const byNumber = useMemo(
    () => new Map(versions.map((entry) => [entry.version, entry])),
    [versions],
  );
  // The version a pick is compared with can sit on a page not read yet: the live version comes
  // with the first page wherever it is, and the oldest version read has its predecessor further
  // back. One read of the page before the pick brings the version just before it, for the
  // comparison only; the list itself still grows a page at a time. Version 1 has nothing before it.
  const beforeKey = to !== null ? `${pagesKey}:${to}` : null;
  const [before, setBefore] = useState<{ key: string; entry: StoreVersion | null } | null>(null);
  const haveBefore = beforeKey !== null && before?.key === beforeKey;
  const needBefore = to !== null && to > 1 && from === null && more.next !== null;
  useEffect(() => {
    if (!needBefore || haveBefore || to === null || beforeKey === null) return;
    let alive = true;
    api.versions(store.id, to).then(
      (page) => alive && setBefore({ key: beforeKey, entry: page.versions[0] ?? null }),
      () => alive && setBefore({ key: beforeKey, entry: null }),
    );
    return () => {
      alive = false;
    };
  }, [needBefore, haveBefore, beforeKey, to, api, store.id]);
  const fromEntry =
    from !== null ? (byNumber.get(from) ?? null) : haveBefore ? (before?.entry ?? null) : null;

  if (list.loading && !list.data) return <LoadingState label={copy.versionsLoading} />;
  if (list.error && !list.data)
    return <ErrorState message={copy.versionsFailed} onRetry={list.refresh} />;

  const restore = async (version: number) => {
    setRestoring('pending');
    try {
      const answer = await api.restore(store.id, version);
      setConfirming(null);
      setPicked(null);
      setRestoring('idle');
      setRestoredTick((tick) => tick + 1);
      onRestored?.(answer.version);
    } catch {
      setRestoring('failed');
    }
  };

  let day = '';
  return (
    <div className="pc-versions-layout sf-versions" data-testid="sf-versions">
      <ul className="pc-versions-list sf-versions-list" aria-label={copy.viewVersions}>
        {versions.map((entry) => {
          const label = dayLabel(entry.savedAt, locale, copy);
          const startsDay = label !== day;
          day = label;
          const isLive = entry.version === live;
          const selected = entry.version === to;
          const passed =
            entry.checks && entry.checks.total > 0 && entry.checks.passed === entry.checks.total;
          return (
            <li
              key={entry.version}
              className="sf-version"
              data-selected={selected ? 'true' : undefined}
              data-testid="sf-version"
              data-version={entry.version}
            >
              {startsDay && (
                <span className="sf-versions-day" role="presentation">
                  {label}
                </span>
              )}
              <div className="sf-version-row">
                <button
                  type="button"
                  className="pc-versions-main"
                  data-testid="sf-version-pick"
                  aria-pressed={selected}
                  onClick={() => {
                    setPicked(entry.version);
                    setConfirming(null);
                  }}
                >
                  <DesignThumb design={entry.design} />
                  <span className="pc-versions-text">
                    <strong>
                      {formatCopy(copy.versionTitle, { version: String(entry.version) })}
                    </strong>
                    {versionWhat(entry, store, copy) && (
                      <span className="sf-version-what">{versionWhat(entry, store, copy)}</span>
                    )}
                    <small>{versionMeta(entry, locale, copy)}</small>
                  </span>
                </button>
                <span className="pc-versions-side">
                  {isLive && <StatusPill tone="live">{copy.versionLive}</StatusPill>}
                  {!isLive && passed && <StatusPill>{copy.versionChecked}</StatusPill>}
                  {entry.version === newest && !isLive && (
                    <a
                      className="ws-button sf-version-publish"
                      href={publishHref}
                      data-testid="sf-version-publish"
                    >
                      {copy.versionPublish}
                    </a>
                  )}
                  {!isLive && entry.version !== newest && (
                    <button
                      type="button"
                      className="ws-button"
                      data-testid="sf-version-restore"
                      data-version={entry.version}
                      onClick={() => {
                        setConfirming(entry.version);
                        setRestoring('idle');
                      }}
                    >
                      {copy.versionRestore}
                    </button>
                  )}
                </span>
              </div>
              {confirming === entry.version && (
                <div
                  className="ap-confirm sf-version-confirm"
                  role="group"
                  aria-label={formatCopy(copy.versionRestoreTitle, {
                    version: String(entry.version),
                  })}
                >
                  <p>
                    <strong>
                      {formatCopy(copy.versionRestoreTitle, { version: String(entry.version) })}
                    </strong>{' '}
                    {formatCopy(copy.versionRestoreBody, {
                      next: String((newest ?? entry.version) + 1),
                    })}
                  </p>
                  {restoring === 'failed' && <p role="alert">{copy.versionRestoreFailed}</p>}
                  <span className="sf-version-confirm-actions">
                    <button
                      type="button"
                      className="ws-button"
                      disabled={restoring === 'pending'}
                      onClick={() => void restore(entry.version)}
                      data-testid="sf-version-restore-confirm"
                    >
                      {copy.versionRestoreConfirm}
                    </button>
                    <button
                      type="button"
                      className="ws-text-button"
                      onClick={() => {
                        // Cancel returns focus to this version's Restore, as Publish's does.
                        setConfirming(null);
                        requestAnimationFrame(() =>
                          document
                            .querySelector<HTMLButtonElement>(
                              `[data-testid="sf-version-restore"][data-version="${entry.version}"]`,
                            )
                            ?.focus(),
                        );
                      }}
                    >
                      {copy.versionRestoreCancel}
                    </button>
                  </span>
                </div>
              )}
            </li>
          );
        })}
        {more.next !== null && (
          <li className="sf-older sf-versions-more">
            <button
              type="button"
              className="ws-button"
              data-testid="sf-versions-more"
              disabled={more.state === 'loading'}
              aria-busy={more.state === 'loading' || undefined}
              onClick={() => void more.load()}
            >
              {copy.showOlderVersions}
            </button>
            {more.state === 'failed' && <small role="status">{copy.loadFailed}</small>}
          </li>
        )}
      </ul>
      {to !== null && fromEntry !== null && (
        <Compare
          store={store}
          from={fromEntry}
          to={byNumber.get(to) ?? null}
          live={live}
          copy={copy}
        />
      )}
    </div>
  );
}

/**
 * The versions read so far, newest first. The first page holds the newest 25 and the live version
 * wherever it is; earlier pages come on request and the live one is listed once. A new key (a
 * publish, a restore, another store) starts again from the newest.
 */
function useVersionPages(api: StorefrontApi, storeId: string, key: string) {
  const list = useRemote(() => api.versions(storeId), key);
  const [older, setOlder] = useState<{ versions: StoreVersion[]; next: number | null } | null>(
    null,
  );
  const [state, setState] = useState<'idle' | 'loading' | 'failed'>('idle');
  const asking = useRef(key);
  asking.current = key;
  useEffect(() => {
    setOlder(null);
    setState('idle');
  }, [key]);
  const next = older ? older.next : (list.data?.next ?? null);
  const load = useCallback(async () => {
    if (next === null) return;
    const askedFor = asking.current;
    setState('loading');
    try {
      const page = await api.versions(storeId, next);
      if (asking.current !== askedFor) return;
      setOlder((current) => ({
        versions: [...(current?.versions ?? []), ...page.versions],
        next: page.next ?? null,
      }));
      setState('idle');
    } catch {
      if (asking.current === askedFor) setState('failed');
    }
  }, [api, storeId, next]);
  const versions = useMemo(() => {
    const byNumber = new Map<number, StoreVersion>();
    for (const entry of [...(list.data?.versions ?? []), ...(older?.versions ?? [])])
      if (!byNumber.has(entry.version)) byNumber.set(entry.version, entry);
    return [...byNumber.values()].sort((a, b) => b.version - a.version);
  }, [list.data, older]);
  return {
    list,
    versions,
    live: list.data?.liveVersion ?? null,
    more: { next, state, load },
  };
}

/** The two versions' real previews on top of each other: the earlier one shows left of the slider. */
function Compare({
  store,
  from,
  to,
  live,
  copy,
}: {
  store: StoreDetail;
  from: StoreVersion | null;
  to: StoreVersion | null;
  live: number | null;
  copy: Copy;
}) {
  const api = useStorefrontApi();
  const [percent, setPercent] = useState(50);
  const before = useRemote(
    from ? () => api.preview(store.id, from.version) : null,
    `${store.id}:from:${from?.version ?? 0}`,
  );
  const after = useRemote(
    to ? () => api.preview(store.id, to.version) : null,
    `${store.id}:to:${to?.version ?? 0}`,
  );
  if (!from || !to) return null;
  const values = { from: String(from.version), to: String(to.version) };
  const tag = (version: number) =>
    formatCopy(version === live ? copy.compareTagLive : copy.compareTag, {
      version: String(version),
    });
  const changes = designChanges(
    from.version < to.version ? from.design : to.design,
    from.version < to.version ? to.design : from.design,
  );
  const [low, high] =
    from.version < to.version ? [from.version, to.version] : [to.version, from.version];
  return (
    <section
      className="pc-compare sf-compare"
      data-testid="sf-compare"
      aria-label={formatCopy(copy.compareTitle, values)}
    >
      <header>
        <h3>
          {formatCopy(from.version === live ? copy.compareTitleLive : copy.compareTitle, values)}
        </h3>
        <span>{copy.compareHint}</span>
      </header>
      <div className="sf-compare-body">
        <div className="sf-compare-col">
          <div className="sf-compare-tags" aria-hidden="true">
            <span>{tag(from.version)}</span>
            <span>{tag(to.version)}</span>
          </div>
          <div className="sf-compare-stage" style={{ '--sf-cmp': `${percent}%` } as CSSProperties}>
            {/* A preview still loading is a quiet tile: never the last version's preview under a new tag. */}
            {(after.loading || before.loading) && (
              <span className="sf-compare-wait" aria-hidden="true" />
            )}
            {after.data && !after.loading && (
              <iframe
                className="sf-compare-after"
                src={after.data.frameUrl}
                sandbox={FRAME_SANDBOX}
                tabIndex={-1}
                title={formatCopy(copy.versionTitle, { version: values.to })}
                data-testid="sf-compare-to"
              />
            )}
            {before.data && !before.loading && (
              <iframe
                className="sf-compare-before"
                src={before.data.frameUrl}
                sandbox={FRAME_SANDBOX}
                tabIndex={-1}
                title={formatCopy(copy.versionTitle, { version: values.from })}
                data-testid="sf-compare-from"
              />
            )}
            <span className="sf-compare-divider" aria-hidden="true" />
          </div>
          <input
            type="range"
            min={0}
            max={100}
            value={percent}
            aria-label={formatCopy(copy.compareLabel, values)}
            data-testid="sf-compare-range"
            onChange={(event) => setPercent(clamp(Number(event.target.value)))}
            onInput={(event) => setPercent(clamp(Number(event.currentTarget.value)))}
          />
        </div>
        <div className="sf-changes" data-testid="sf-changes">
          <h4>{formatCopy(copy.changesTitle, { from: String(low), to: String(high) })}</h4>
          {changes.length ? (
            changes.map((change) => <ChangeRow key={change.field} change={change} copy={copy} />)
          ) : (
            <p>{copy.changesNone}</p>
          )}
        </div>
      </div>
    </section>
  );
}

/** A language's name in the reader's language; its code when the browser has no name for it. */
function languageName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

function ChangeRow({ change, copy }: { change: DesignChange; copy: Copy }) {
  const locale = useAppCatalogLocale() ?? 'en';
  // Words in another language read as the store's own field, with the language named.
  const [group, language, name] = change.field.split('.');
  const label =
    group === 'translations' && language && name
      ? formatCopy(copy.fieldInLanguage, {
          field: copy[`field.copy.${name}` as StorefrontCopyKey] ?? name,
          language: languageName(language, locale),
        })
      : (copy[`field.${change.field}` as StorefrontCopyKey] ?? change.field);
  const kind =
    change.kind === 'added'
      ? copy.changeAdded
      : change.kind === 'removed'
        ? copy.changeRemoved
        : copy.changeChanged;
  return (
    <p className="sf-diff" data-change={change.kind} data-testid="sf-diff">
      <span className="sf-diff-head">
        <StatusPill>{kind}</StatusPill>
        <b>{label}</b>
      </span>
      {change.field !== 'css' && change.field !== 'logo.svg' && (
        <span className="sf-diff-values">
          {change.from !== null && (
            <>
              <del>
                <Value value={change.from} copy={copy} />
              </del>
              {change.to !== null && <span aria-hidden="true"> → </span>}
            </>
          )}
          {change.to !== null && (
            <ins>
              <Value value={change.to} copy={copy} />
            </ins>
          )}
        </span>
      )}
    </p>
  );
}

function Value({ value, copy }: { value: DesignValue; copy: Copy }) {
  if (typeof value === 'string' && isColour(value))
    return (
      <span className="sf-diff-colour">
        <i style={{ background: value }} aria-hidden="true" />
        {value}
      </span>
    );
  if (typeof value === 'boolean') return <>{value ? copy.changeYes : copy.changeNo}</>;
  if (Array.isArray(value)) return <>{value.map((item) => `“${item}”`).join(' · ')}</>;
  if (typeof value === 'string') {
    const named = copy[`value.${value}` as StorefrontCopyKey];
    if (named) return <>{named}</>;
    return <>{value.length > 90 ? `“${value.slice(0, 88)}…”` : `“${value}”`}</>;
  }
  return <>{String(value)}</>;
}

/** A small drawing of a design's colours: header, mark, lines and a button, as a version's tile. */
function DesignThumb({ design }: { design: StoreDesign }) {
  const colors = (design.colors ?? {}) as Record<string, string | undefined>;
  const panel = colourOr(colors.panel, '#ffffff');
  const style = {
    '--t-panel': panel,
    '--t-head': colourOr(colors.header, panel),
    '--t-brand': colourOr(colors.brand, '#1f3d34'),
    '--t-ink': colourOr(colors.ink, '#15171a'),
    '--t-act':
      colors.actions === 'brand'
        ? colourOr(colors.brand, '#1f3d34')
        : colourOr(colors.actions, colourOr(colors.ink, '#15171a')),
  } as CSSProperties;
  return (
    <span className="sf-version-thumb" style={style} aria-hidden="true">
      <span className="sf-thumb-head">
        <i />
      </span>
      <span className="sf-thumb-line" />
      <span className="sf-thumb-line short" />
      <span className="sf-thumb-act" />
    </span>
  );
}

const colourOr = (value: string | undefined, fallback: string) =>
  value && isColour(value) ? value : fallback;
const clamp = (value: number) => (Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 50);

function versionWhat(entry: StoreVersion, store: StoreDetail, copy: Copy): string | null {
  if (entry.by.kind === 'reading')
    return formatCopy(copy.versionFromReading, { domain: store.domain });
  return entry.summary ? `“${entry.summary}”` : null;
}

function versionMeta(entry: StoreVersion, locale: string, copy: Copy): string {
  const parts: string[] = [];
  if (entry.by.kind === 'person')
    parts.push(
      entry.by.name
        ? formatCopy(copy.versionByPerson, { name: entry.by.name })
        : copy.versionByChat,
    );
  if (entry.savedAt) parts.push(timeOf(entry.savedAt, locale));
  if (entry.publishedAt) {
    const at = dateTimeOf(entry.publishedAt, locale);
    parts.push(
      entry.publishedBy
        ? formatCopy(copy.versionPublishedBy, { name: entry.publishedBy, at })
        : formatCopy(copy.versionPublishedAt, { at }),
    );
  }
  return parts.join(' · ');
}

// A version saved before versions kept their time sits under "Earlier", never on a made-up date.
function dayLabel(iso: string | null, locale: string, copy: Copy): string {
  if (!iso || Number.isNaN(Date.parse(iso))) return copy.versionEarlier;
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return copy.versionToday;
  if (date.toDateString() === yesterday.toDateString()) return copy.versionYesterday;
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(date);
}

const timeOf = (iso: string, locale: string) =>
  new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
    new Date(iso),
  );
const dateTimeOf = (iso: string, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
